import React, { useEffect, useState } from 'react';
import { api } from '../services/api';
import useInvalidate from '../hooks/useInvalidate';

function CatRow({
  cat,
  nested,
  editing,
  editNombre,
  onEditNombre,
  onSaveNombre,
  onCancelEdit,
  onStartEdit,
  onMover,
  onBorrar,
}) {
  const base = Number(cat.id) >= 2 && Number(cat.id) <= 8 && !nested;
  return (
    <tr className={nested ? 'cat-row-hija' : ''}>
      <td data-label="Nombre">
        {nested ? <span className="cat-nested-mark">↳</span> : null}
        {editing ? (
          <input
            className="cat-edit-input"
            value={editNombre}
            onChange={(e) => onEditNombre(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onSaveNombre(cat.id);
              if (e.key === 'Escape') onCancelEdit();
            }}
            autoFocus
          />
        ) : (
          <strong>
            {cat.label || cat.nombre}
            {nested ? (
              <span className="cat-badge">Subcategoría</span>
            ) : (
              <span className="cat-badge cat-badge-padre">Categoría</span>
            )}
          </strong>
        )}
      </td>
      <td data-label="Productos">{cat.total != null ? cat.total : '—'}</td>
      <td data-label="Acciones" className="cat-actions">
        {editing ? (
          <>
            <button type="button" className="btn-primary" onClick={() => onSaveNombre(cat.id)}>
              Guardar
            </button>
            <button type="button" className="ghost" onClick={onCancelEdit}>
              Cancelar
            </button>
          </>
        ) : (
          <>
            <button type="button" className="ghost" onClick={() => onMover(cat.id, 'up')} title="Subir">
              ↑
            </button>
            <button type="button" className="ghost" onClick={() => onMover(cat.id, 'down')} title="Bajar">
              ↓
            </button>
            <button type="button" className="ghost" onClick={() => onStartEdit(cat)}>
              Renombrar
            </button>
            {base ? (
              <span className="muted">Base</span>
            ) : (
              <button type="button" className="ghost cat-del" onClick={() => onBorrar(cat)}>
                Borrar
              </button>
            )}
          </>
        )}
      </td>
    </tr>
  );
}

export default function CategoriasPage({ pin, onToast }) {
  const [arbol, setArbol] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nombre, setNombre] = useState('');
  const [parentId, setParentId] = useState('1');
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editNombre, setEditNombre] = useState('');

  function load() {
    setLoading(true);
    return api
      .getAdminCategorias()
      .then((res) => setArbol(res.data || []))
      .catch((err) => {
        setArbol([]);
        onToast?.(err.message || 'No se pudieron cargar las categorías', 'error');
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    load();
  }, [pin]);

  useInvalidate(['product'], load);

  async function crear(e) {
    e.preventDefault();
    const nom = nombre.trim();
    if (!nom) return;
    setSaving(true);
    try {
      await api.createCategoria({
        nombre: nom,
        parent_id: parentId === '1' ? 1 : Number(parentId),
      });
      setNombre('');
      setParentId('1');
      onToast?.('Categoría creada. Los productos de esta categoría salen juntos en admin, vendedor y tienda.');
      await load();
    } catch (err) {
      onToast?.(err.message || 'No se pudo crear', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function guardarNombre(id) {
    const nom = editNombre.trim();
    if (!nom) return;
    try {
      await api.updateCategoria(id, { nombre: nom });
      setEditId(null);
      onToast?.('Nombre actualizado');
      await load();
    } catch (err) {
      onToast?.(err.message || 'No se pudo guardar', 'error');
    }
  }

  async function mover(id, direccion) {
    try {
      await api.moverCategoria(id, direccion);
      await load();
    } catch (err) {
      onToast?.(err.message || 'No se pudo mover', 'error');
    }
  }

  async function borrar(cat) {
    const ok = window.confirm(
      `¿Borrar "${cat.nombre}"? Solo si no tiene productos ni subcategorías.`
    );
    if (!ok) return;
    try {
      await api.deleteCategoria(cat.id);
      onToast?.('Categoría desactivada');
      await load();
    } catch (err) {
      onToast?.(err.message || 'No se pudo borrar', 'error');
    }
  }

  const rowProps = {
    editNombre,
    onEditNombre: setEditNombre,
    onSaveNombre: guardarNombre,
    onCancelEdit: () => setEditId(null),
    onStartEdit: (cat) => {
      setEditId(cat.id);
      setEditNombre(cat.nombre);
    },
    onMover: mover,
    onBorrar: borrar,
  };

  return (
    <div className="panel">
      <header className="panel-head">
        <div>
          <h2>Categorías y subcategorías</h2>
          <p className="muted">
            El orden de aquí es el mismo en Productos, app de vendedor y tienda: primero la categoría,
            luego la subcategoría, y dentro los productos de la misma familia juntos.
          </p>
        </div>
      </header>

      <form className="form-row categorias-form" onSubmit={crear}>
        <input
          placeholder="Nombre *"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          required
        />
        <select value={parentId} onChange={(e) => setParentId(e.target.value)}>
          <option value="1">Categoría principal (nueva)</option>
          {arbol.map((p) => (
            <option key={p.id} value={String(p.id)}>
              Subcategoría de {p.nombre}
            </option>
          ))}
        </select>
        <button type="submit" disabled={saving || !nombre.trim()}>
          {saving ? 'Creando…' : parentId === '1' ? 'Crear categoría' : 'Crear subcategoría'}
        </button>
      </form>

      {loading && !arbol.length ? <p className="muted">Cargando…</p> : null}
      <div className="table-wrap">
        <table className="responsive-cards">
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Productos</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {arbol.map((p) => (
              <React.Fragment key={p.id}>
                <CatRow
                  cat={p}
                  nested={false}
                  editing={editId === p.id}
                  {...rowProps}
                />
                {(p.hijas || []).map((h) => (
                  <CatRow
                    key={h.id}
                    cat={h}
                    nested
                    editing={editId === h.id}
                    {...rowProps}
                  />
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
